import { useEffect, useRef } from 'react';
import type { gsap as GsapNamespace } from 'gsap';
import type { ScrollTrigger as ScrollTriggerClass } from 'gsap/ScrollTrigger';

/* ═══════════════════════════════════════════════════════════════════════
   Capa de movimiento de la landing
   ═══════════════════════════════════════════════════════════════════════

   Por qué GSAP se carga con import() dinámico y no arriba del archivo:
   `LandingPage` se importa de forma ESTÁTICA en App.tsx (es la primera
   pantalla del visitante anónimo, así que no puede ir detrás de un Suspense
   sin meterle una cascada al LCP). Cargado así, el navegador lo pide después
   del primer pintado y solo en la landing; el resto de la app no paga su peso.
   Ojo: vite.config.ts le da su propio trozo (`vendor-gsap`), porque el comodín
   de `manualChunks` lo metía en el bundle común y anulaba todo esto.

   Consecuencia de diseño: la página tiene que verse terminada ANTES de que
   GSAP llegue. Por eso toda entrada se escribe con `gsap.from()` sobre el
   estado final ya pintado en CSS.

   ── DOS NIVELES DE MOVIMIENTO, NO UN INTERRUPTOR ──────────────────────

   La primera versión apagaba TODO con `prefers-reduced-motion: reduce`. Eso
   estaba mal por dos motivos. Uno técnico: la norma pide reducir el movimiento
   VESTIBULAR —paralaje, escalas, recorridos largos, cosas que se mueven solas—,
   no prohibir que un elemento aparezca. Y otro práctico: el dueño del proyecto
   tiene esa preferencia activada en Windows, así que veía una página
   completamente muerta y con razón preguntaba dónde estaba el trabajo.

   Ahora hay dos niveles y cada sección elige:

     `motion(fn)`  — solo con movimiento pleno. Paralaje, pin, escala, scrub.
     `reveal(...)` — entradas. SIEMPRE corren; con movimiento reducido se
                     degradan a fundido puro, sin desplazamiento.
     `always(fn)`  — información, no decoración (qué paso está activo, etc.).

   `gsap.matchMedia` re-ejecuta el bloque cuando la preferencia cambia en vivo
   y revierte lo anterior, así que no hay que escuchar nada a mano.
   ═══════════════════════════════════════════════════════════════════════ */

export interface GsapBundle {
    gsap: typeof GsapNamespace;
    ScrollTrigger: typeof ScrollTriggerClass;
}

let pending: Promise<GsapBundle> | null = null;

/** Carga GSAP + ScrollTrigger una sola vez por sesión y registra el plugin. */
export function loadGsap(): Promise<GsapBundle> {
    if (!pending) {
        pending = Promise.all([
            import('gsap'),
            import('gsap/ScrollTrigger'),
        ]).then(([core, plugin]) => {
            core.gsap.registerPlugin(plugin.ScrollTrigger);
            return { gsap: core.gsap, ScrollTrigger: plugin.ScrollTrigger };
        }).catch((err) => {
            // El trozo ya no existe (despliegue nuevo, pestaña vieja). Guardar
            // la promesa fallida dejaba TODAS las animaciones muertas hasta
            // recargar: cada llamada recibía el mismo rechazo. Se suelta para
            // que la siguiente lo vuelva a intentar.
            pending = null;
            throw err;
        });
    }
    return pending;
}

/* ── Curvas de la casa ───────────────────────────────────────────────────
   Todas de salida, ninguna con rebote ni elástico.

   Se suavizaron a petición del equipo («que se sienta un poco más suave»). Lo
   que se sentía brusco era el ARRANQUE: `expo.out` y `power3.out` gastan casi
   la mitad del recorrido en los primeros milisegundos, así que el ojo percibe
   un latigazo y luego una cola larga. Bajar un grado de potencia —y, en el
   acomodo de las fotos, pasar a una curva que también entra despacio— quita el
   tirón sin quitar el gesto. */
export const EASE = {
    /** Entradas de texto y elementos: entra y se posa, sin latigazo. */
    out: 'power2.out',
    /** Titulares: un punto más de carácter, pero sin el tirón de `expo`. */
    titular: 'power2.out',
    /**
     * Acomodos largos (las fotografías): arranca despacio Y frena despacio.
     * Es la curva que hace que un movimiento grande se sienta caro en vez de
     * repentino.
     */
    asiento: 'power2.inOut',
    /** Recorridos atados al scroll — sin easing, o se despega del dedo. */
    none: 'none',
} as const;

export interface RevealOpts {
    /** Elemento que dispara. Por defecto, la raíz de la sección. */
    trigger?: Element | null;
    start?: string;
    stagger?: number;
    /** Desplazamiento vertical con movimiento pleno. Con reducido siempre 0. */
    y?: number;
    duration?: number;
    delay?: number;
    /**
     * Vuelve a correr CADA VEZ que el elemento entra en pantalla, en lugar de
     * una sola vez en la vida de la página.
     *
     * Se reserva a los momentos grandes —titulares, fotografías—. En el cuerpo
     * de texto y en las filas de una lista es contraproducente: si alguien sube
     * a releer un párrafo, no quiere que el párrafo se vuelva a montar delante
     * de sus ojos.
     */
    repetir?: boolean;
}

/**
 * Repite la entrada CADA VEZ que el elemento vuelve a asomar, venga el scroll
 * de arriba o de abajo. Los cuatro valores de `toggleActions` son, en orden:
 * al entrar · al salir · al volver a entrar (subiendo) · al volver a salir.
 */
export const REPITE = 'restart none restart none';

export interface SectionMotionApi extends GsapBundle {
    /** Raíz de la sección. Todo selector se resuelve dentro de ella. */
    scope: HTMLElement;
    /** `true` si el usuario acepta movimiento amplio. */
    full: boolean;
    /** Solo con movimiento pleno: paralaje, pin, escala, scrub. */
    motion: (build: () => void) => void;
    /** Corre siempre: cambios de estado que son información, no adorno. */
    always: (build: () => void) => void;
    /** Entrada estándar. Con movimiento reducido se queda en fundido. */
    reveal: (targets: string | Element | Element[] | NodeListOf<Element>, opts?: RevealOpts) => void;
    /**
     * Entrada de un enunciado grande, palabra por palabra (ver `Frase.tsx`).
     * Se repite cada vez que el titular vuelve a entrar en pantalla.
     */
    titular: (opts?: { selector?: string; trigger?: Element | null; start?: string }) => void;
}

/**
 * Monta las animaciones de una sección dentro de un `gsap.context` acotado a
 * su propia raíz.
 *
 * El context hace tres cosas que a mano se olvidan: acota los selectores a la
 * sección (dos secciones pueden usar `.paso` sin pisarse), revierte TODO al
 * desmontar (incluidos los ScrollTrigger y los matchMedia creados dentro), y
 * sobrevive al doble montaje de StrictMode en React 19.
 */
export function useSectionMotion<T extends HTMLElement = HTMLDivElement>(
    setup: (api: SectionMotionApi) => void,
) {
    const ref = useRef<T>(null);
    // El callback se guarda en una ref: el efecto corre una sola vez, pero así
    // nunca opera sobre un closure viejo si el componente vuelve a renderizar.
    const setupRef = useRef(setup);
    setupRef.current = setup;

    useEffect(() => {
        const root = ref.current;
        if (!root) return;

        let mounted = true;
        let ctx: ReturnType<typeof GsapNamespace.context> | undefined;

        loadGsap().then(({ gsap, ScrollTrigger }) => {
            if (!mounted || !ref.current) return;

            ctx = gsap.context(() => {
                const mm = gsap.matchMedia();
                mm.add(
                    {
                        pleno: '(prefers-reduced-motion: no-preference)',
                        quieto: '(prefers-reduced-motion: reduce)',
                    },
                    (mmCtx) => {
                        const full = Boolean(mmCtx.conditions?.pleno);

                        const reveal: SectionMotionApi['reveal'] = (targets, opts = {}) => {
                            const els =
                                typeof targets === 'string'
                                    ? root.querySelectorAll(targets)
                                    : targets;
                            if (!els || ('length' in els && els.length === 0)) return;
                            gsap.from(els as Element[], {
                                autoAlpha: 0,
                                // El desplazamiento es lo único que desaparece con
                                // movimiento reducido; la aparición se queda.
                                y: full ? (opts.y ?? 22) : 0,
                                duration: full ? (opts.duration ?? 1.05) : 0.7,
                                stagger: opts.stagger ?? 0.08,
                                delay: opts.delay ?? 0,
                                ease: EASE.out,
                                scrollTrigger: {
                                    trigger: opts.trigger ?? root,
                                    start: opts.start ?? 'top 75%',
                                    end: 'bottom 15%',
                                    /* La propiedad se OMITE cuando no se repite;
                                       no se pasa `undefined`. ScrollTrigger no
                                       distingue «ausente» de «indefinido»: hace
                                       `.split(' ')` sobre lo que reciba y revienta
                                       antes de crear el disparador, dejando el
                                       elemento congelado en su estado inicial. */
                                    ...(opts.repetir ? { toggleActions: REPITE } : {}),
                                },
                            });
                        };

                        const titular: SectionMotionApi['titular'] = (opts = {}) => {
                            const els = root.querySelectorAll(
                                opts.selector ?? '[data-frase="titular"]',
                            );
                            if (!els.length) return;
                            gsap.from(els, {
                                // Con movimiento pleno las palabras suben desde
                                // detrás del renglón; con reducido solo aparecen.
                                // 112 y no 118: lo justo para que la palabra
                                // quepa entera bajo su máscara. Cuanto más
                                // recorrido, más rápido tiene que ir para durar
                                // lo mismo, y más se siente el tirón.
                                yPercent: full ? 112 : 0,
                                autoAlpha: full ? 1 : 0,
                                duration: full ? 1.2 : 0.75,
                                // Cascada un poco más abierta: las palabras se
                                // encadenan en vez de salir casi a la vez.
                                stagger: full ? 0.065 : 0.04,
                                ease: full ? EASE.titular : EASE.out,
                                scrollTrigger: {
                                    trigger: opts.trigger ?? root,
                                    start: opts.start ?? 'top 78%',
                                    end: 'bottom 15%',
                                    toggleActions: REPITE,
                                },
                            });
                        };

                        setupRef.current({
                            gsap,
                            ScrollTrigger,
                            scope: root,
                            full,
                            reveal,
                            titular,
                            motion: (build) => { if (full) build(); },
                            always: (build) => build(),
                        });
                    },
                );
            }, ref);
        });

        return () => {
            mounted = false;
            ctx?.revert();
        };
    }, []);

    return ref;
}

/**
 * Desplazamiento suave de toda la página (Lenis) sincronizado con ScrollTrigger.
 *
 * Es lo que más cambia la SENSACIÓN de una página: el scroll deja de ir a
 * saltos de rueda y pasa a tener inercia, y todo lo que va atado al scroll
 * —paralajes, paneles pegados, recorridos— se mueve con esa misma inercia en
 * vez de a tirones.
 *
 * Va apagado con `prefers-reduced-motion`. Aquí no hay término medio posible:
 * el scroll suave ES movimiento inducido, justo lo que esa preferencia pide
 * evitar, y secuestrar la rueda de alguien que pidió lo contrario es de mala
 * educación.
 *
 * El bucle lo lleva el ticker de GSAP y no un `requestAnimationFrame` propio:
 * dos bucles independientes se desincronizan y el paralaje tiembla.
 */
export function useSmoothScroll() {
    useEffect(() => {
        if (typeof window === 'undefined') return;

        /* ── Todo lo del efecto se DECLARA LO PRIMERO ─────────────────
           `alPulsarAncla` cierra sobre `lenis`, y en pantalla táctil el efecto
           se va por el `return` de más abajo sin llegar nunca hasta aquí: la
           ligadura se quedaba para siempre en la zona muerta temporal y cada
           pulsación en un ancla reventaba con `ReferenceError` —después del
           `preventDefault()`, así que el toque se cancelaba y la página no se
           movía—. Se llevaba por delante el menú entero, el logo, el CTA y el
           salto al contenido en teléfono y tableta.

           Declaradas aquí arriba, en el camino táctil `lenis` vale `undefined`
           y el manejador cae solo en su respaldo con `scrollIntoView`. NO las
           bajes: no basta con ponerlas encima del `return` táctil, porque
           entonces cualquier cosa que se añada entre el manejador y ellas
           vuelve a armar la misma trampa. */
        let cancelado = false;
        let lenis:
            | {
                  raf: (t: number) => void;
                  destroy: () => void;
                  scrollTo: (t: HTMLElement, o?: { duration?: number }) => void;
              }
            | undefined;

        let tick: ((t: number) => void) | undefined;
        let gsapRef: typeof GsapNamespace | undefined;

        const quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /* ── Los saltos de ancla deslizan SIEMPRE ────────────────────────
           Antes esto salía por la puerta de atrás con movimiento reducido, y
           con ello se iba también el deslizamiento del menú: pulsabas
           «Academy» y la página pegaba un corte seco. Un salto que pide el
           usuario no es movimiento impuesto, y ver el recorrido es lo que te
           dice DÓNDE has caído; sin él, aterrizas sin saber de dónde vienes.
           Con Lenis lo lleva Lenis; sin Lenis, el propio navegador, que además
           respeta `scroll-padding-top` por su cuenta. */
        const alPulsarAncla = (e: MouseEvent) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
            const ancla = (e.target as HTMLElement)?.closest?.('a[href^="#"]');
            if (!ancla) return;
            const href = ancla.getAttribute('href');
            if (!href || href === '#') return;
            const destino = document.querySelector(href);
            if (!destino) return;
            e.preventDefault();
            if (lenis) lenis.scrollTo(destino as HTMLElement, { duration: 1.3 });
            else destino.scrollIntoView({ behavior: 'smooth', block: 'start' });

            /* La almohadilla NO se escribe en la barra de direcciones. Pedido
               explícitamente: molestaba ver `/#empezar` al pulsar el menú.
               Aquí había un `pushState(null, '', href)` que la ponía a mano;
               como el manejador ya hace `preventDefault()`, esa línea no
               participaba en el salto — su único efecto era ensuciar la barra.

               El `replaceState` limpia una almohadilla que venga de fuera (por
               ejemplo si alguien abrió `/#empezar` desde un enlace viejo) para
               que no se quede pegada al navegar por el menú. `replaceState` no
               añade entrada al historial ni dispara `hashchange`.

               Lo que se pierde, aceptado a sabiendas: ya no se puede compartir
               el enlace a una sección concreta, y «atrás» deja de recorrer los
               saltos. Llegar de fuera a `/#empezar` SÍ sigue funcionando: eso
               lo resuelve el navegador en la primera carga, antes de que este
               manejador exista.

               La sección marcada en el menú no se entera: se calcula midiendo
               posiciones de scroll (ver `medir()` en LandingNav), no leyendo
               la barra de direcciones. */
            if (window.location.hash) {
                history.replaceState(null, '', window.location.pathname + window.location.search);
            }
        };
        document.addEventListener('click', alPulsarAncla);

        /* ── Lenis NO se monta en pantallas táctiles ──────────────────────
           Lenis suaviza la RUEDA del ratón; el scroll táctil lo deja pasar al
           navegador (`syncTouch` va desactivado, y activarlo pelea con el
           rebote nativo del sistema). O sea que en un teléfono monta un bucle
           de `requestAnimationFrame` que no suaviza nada y, medido en un móvil
           de gama media, se llevaba ~485 ms de hilo principal en `scrollTo`
           más el coste de actualizar ScrollTrigger en cada cuadro.
           El scroll nativo del teléfono ya va acelerado por hardware y es más
           fluido que cualquier cosa que podamos poner encima. */
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            return () => document.removeEventListener('click', alPulsarAncla);
        }

        (async () => {
            const [{ default: Lenis }, { gsap, ScrollTrigger }] = await Promise.all([
                import('lenis'),
                loadGsap(),
            ]);
            if (cancelado) return;
            gsapRef = gsap;

            lenis = new Lenis({
                /* 1,45 s de inercia. Subido desde 1,25 porque el equipo seguía
                   diciendo «se siente, pero muy poco»: este número ES la
                   sensación de scroll. Más arriba de ~1,6 empieza a sentirse
                   flotante y cuesta apuntar a un sitio concreto.

                   Con movimiento reducido NO se apaga, se ACORTA a 0,8 s. Aquí
                   la página no se mueve sola: se mueve porque el usuario gira
                   la rueda, y lo único que cambia es cuánto tarda en frenar.
                   Apagarlo del todo dejaba el sitio con el tacto de scroll de
                   un documento de texto, que es exactamente lo que el equipo
                   reportaba como «no se siente». Lo que SÍ sigue apagado con
                   esa preferencia es todo lo que se mueve sin que nadie lo
                   toque: paralajes, escalas y el apilado del hero. */
                duration: quieto ? 1.15 : 1.45,
                // Exponencial de salida: entra rápido y se posa, sin rebote.
                easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            });

            (lenis as unknown as { on: (e: string, cb: () => void) => void }).on(
                'scroll',
                ScrollTrigger.update,
            );

            tick = (time: number) => lenis?.raf(time * 1000);
            gsap.ticker.add(tick);
            // Sin esto, un tirón de carga (una imagen pesada que entra) hace que
            // GSAP «recupere» tiempo y el scroll pegue un salto.
            gsap.ticker.lagSmoothing(0);

        })().catch((err) => {
            // Sin scroll suave, la página sigue siendo la página: el scroll
            // nativo ya funciona. Antes esto salía como «unhandledrejection»
            // (vendor-motion tras un despliegue) y parecía un fallo de la app.
            console.info('[landing] sin scroll suave: no cargó el módulo de animación —', (err as Error)?.message ?? err);
        });

        return () => {
            cancelado = true;
            document.removeEventListener('click', alPulsarAncla);
            if (tick && gsapRef) gsapRef.ticker.remove(tick);
            gsapRef?.ticker.lagSmoothing(500, 33);
            lenis?.destroy();
        };
    }, []);
}

/**
 * Botón magnético: la pieza se inclina hacia el cursor cuando te acercas y
 * vuelve a su sitio cuando te alejas.
 *
 * Es el detalle táctil que separa un botón de una caja con texto: antes de
 * hacer clic ya sabes que responde. Recorrido corto a propósito (10 px como
 * máximo, un cuarto de la distancia real): pasado ese punto el botón huye del
 * cursor y se vuelve incómodo de apuntar, que es el error clásico de este
 * efecto.
 *
 * `quickTo` en vez de un `gsap.to` por evento: crea el tween UNA vez y luego
 * solo le cambia el destino, así un `pointermove` que dispara sesenta veces por
 * segundo no genera sesenta animaciones.
 *
 * Solo con ratón. En una pantalla táctil no hay puntero al que acercarse y el
 * efecto sencillamente no existe.
 */
export function useMagnetico<T extends HTMLElement = HTMLButtonElement>() {
    const ref = useRef<T>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        /* Sin comprobar `prefers-reduced-motion`: son 10 px que provoca el
           propio cursor al acercarse. No hay movimiento autónomo que evitar. */
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        let vivo = true;
        let limpiar: (() => void) | undefined;

        loadGsap().then(({ gsap }) => {
            if (!vivo || !ref.current) return;

            const aX = gsap.quickTo(el, 'x', { duration: 0.5, ease: EASE.out });
            const aY = gsap.quickTo(el, 'y', { duration: 0.5, ease: EASE.out });

            const RADIO = 90;   // px de margen alrededor del botón donde ya atrae
            const FUERZA = 0.25;
            const TOPE = 10;

            const alMover = (e: PointerEvent) => {
                const r = el.getBoundingClientRect();
                const dx = e.clientX - (r.left + r.width / 2);
                const dy = e.clientY - (r.top + r.height / 2);
                const dentro =
                    Math.abs(dx) < r.width / 2 + RADIO && Math.abs(dy) < r.height / 2 + RADIO;
                if (!dentro) {
                    aX(0);
                    aY(0);
                    return;
                }
                aX(gsap.utils.clamp(-TOPE, TOPE, dx * FUERZA));
                aY(gsap.utils.clamp(-TOPE, TOPE, dy * FUERZA));
            };

            const soltar = () => {
                aX(0);
                aY(0);
            };

            window.addEventListener('pointermove', alMover, { passive: true });
            // Al perder el foco de la ventana el puntero deja de reportarse y el
            // botón se quedaría desplazado para siempre.
            window.addEventListener('blur', soltar);
            limpiar = () => {
                window.removeEventListener('pointermove', alMover);
                window.removeEventListener('blur', soltar);
                gsap.set(el, { x: 0, y: 0 });
            };
        });

        return () => {
            vivo = false;
            limpiar?.();
        };
    }, []);

    return ref;
}

/**
 * Hover que sabe por dónde entraste.
 *
 * En una lista de filas, el fondo del hover que simplemente se funde no dice
 * nada: es el mismo gesto vengas de donde vengas. Aquí la superficie ENTRA por
 * el mismo canto que cruzó el puntero y SALE por el canto por donde te vas, así
 * que el movimiento continúa el tuyo en vez de interrumpirlo. Es lo que separa
 * una lista de enlaces de una lista que se siente hecha a mano.
 *
 * Cómo se decide el canto: se mira dónde cayó el puntero dentro del rectángulo
 * y se toma el borde más cercano. En filas anchas y bajas eso da casi siempre
 * arriba o abajo, que es justo lo que pasa de verdad al recorrer una lista.
 *
 * Degradado en tres escalones, del más capaz al menos:
 *   · ratón + JS ....... barrido direccional (esto);
 *   · dedo, teclado o
 *     movimiento reducido  fundido en el sitio, por CSS (`group-hover` y
 *                          `group-focus-visible` en el propio marcado);
 *   · sin JS ........... lo mismo, porque el fundido nunca dependió de JS.
 *
 * @param selector  qué superficies mover, buscadas dentro del contenedor.
 */
export function useHoverDireccional<T extends HTMLElement = HTMLElement>(
    selector = '[data-superficie]',
) {
    const ref = useRef<T>(null);

    useEffect(() => {
        const cont = ref.current;
        if (!cont) return;
        /* Sin ratón no hay «por dónde entró»: un dedo aparece dentro de la fila
           sin cruzar ningún canto. Se deja el fundido de CSS. */
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        // Con movimiento reducido tampoco: barrer una superficie ES movimiento.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        let vivo = true;
        let limpiar: (() => void) | undefined;

        loadGsap().then(({ gsap }) => {
            if (!vivo || !ref.current) return;

            const superficies = [...cont.querySelectorAll<HTMLElement>(selector)];
            if (!superficies.length) return;

            /* Escribir el estilo en línea desde ya es lo que apaga el fundido de
               CSS: `group-hover:opacity-100` es una clase, y cualquier estilo en
               línea le gana. A partir de aquí manda GSAP.

               Y se quita la transición: si se deja, el `opacity: 1` de la
               entrada tarda 200 ms en cumplirse y la superficie se funde
               mientras entra, con lo que el canto —que es TODO el efecto— llega
               desdibujado. Medido: a los 70 ms de cruzar, la opacidad iba por
               0,44. Aquí la opacidad es un interruptor y el movimiento lo pone
               el barrido. */
            gsap.set(superficies, {
                opacity: 0,
                xPercent: 0,
                yPercent: 0,
                transition: 'none',
            });

            /** Canto más cercano al punto donde está el puntero. */
            const canto = (el: HTMLElement, e: PointerEvent) => {
                const r = el.getBoundingClientRect();
                const x = (e.clientX - r.left) / r.width;    // 0 izquierda … 1 derecha
                const y = (e.clientY - r.top) / r.height;    // 0 arriba … 1 abajo
                const d = [
                    { c: 'arriba', v: y },
                    { c: 'abajo', v: 1 - y },
                    { c: 'izq', v: x },
                    { c: 'der', v: 1 - x },
                ];
                return d.reduce((a, b) => (b.v < a.v ? b : a)).c;
            };

            const fuera = (c: string) =>
                c === 'arriba' ? { yPercent: -100, xPercent: 0 }
                : c === 'abajo' ? { yPercent: 100, xPercent: 0 }
                : c === 'izq' ? { xPercent: -100, yPercent: 0 }
                : { xPercent: 100, yPercent: 0 };

            const quitar: Array<() => void> = [];

            for (const sup of superficies) {
                // La zona sensible es el enlace, no la superficie: la superficie
                // va detrás del texto y no recibe punteros.
                const fila = sup.parentElement;
                if (!fila) continue;

                const entrar = (e: PointerEvent) => {
                    gsap.killTweensOf(sup);
                    gsap.set(sup, { opacity: 1, ...fuera(canto(fila, e)) });
                    gsap.to(sup, { xPercent: 0, yPercent: 0, duration: 0.42, ease: EASE.out });
                };

                const salir = (e: PointerEvent) => {
                    gsap.killTweensOf(sup);
                    gsap.to(sup, {
                        ...fuera(canto(fila, e)),
                        duration: 0.34,
                        ease: 'power2.in',
                        // Devolverla a invisible evita que un cambio de tamaño
                        // de ventana la descubra parada en un canto.
                        onComplete: () => gsap.set(sup, { opacity: 0 }),
                    });
                };

                /* Con el teclado no hay puntero ni canto que cruzar, así que
                   aparece en el sitio. Sin esto, tabular por la lista no
                   marcaría dónde estás. */
                const enfocar = () => {
                    gsap.killTweensOf(sup);
                    gsap.to(sup, { opacity: 1, xPercent: 0, yPercent: 0, duration: 0.2 });
                };
                const desenfocar = () => {
                    gsap.killTweensOf(sup);
                    gsap.to(sup, { opacity: 0, duration: 0.2 });
                };

                fila.addEventListener('pointerenter', entrar);
                fila.addEventListener('pointerleave', salir);
                fila.addEventListener('focus', enfocar);
                fila.addEventListener('blur', desenfocar);
                quitar.push(() => {
                    fila.removeEventListener('pointerenter', entrar);
                    fila.removeEventListener('pointerleave', salir);
                    fila.removeEventListener('focus', enfocar);
                    fila.removeEventListener('blur', desenfocar);
                });
            }

            limpiar = () => {
                quitar.forEach((f) => f());
                // Se borra el estilo en línea para devolverle el mando a CSS.
                gsap.set(superficies, { clearProps: 'all' });
            };
        });

        return () => {
            vivo = false;
            limpiar?.();
        };
    }, [selector]);

    return ref;
}

/**
 * ScrollTrigger mide posiciones al crearse. Las fuentes web llegan después y
 * recomponen los titulares, así que cada trigger creado antes queda calculado
 * contra una altura que ya no existe. Un solo refresh cuando las fuentes
 * terminan de cargar arregla toda la página.
 */
export function refreshOnFontsReady() {
    if (typeof document === 'undefined' || !('fonts' in document)) return;
    document.fonts.ready
        .then(() => loadGsap())
        .then(({ ScrollTrigger }) => ScrollTrigger.refresh())
        .catch(() => { /* sin fuentes web las medidas iniciales ya son buenas */ });
}
